/**
 * ProvisioningModal.tsx
 * Feature: 155 - Telemetry Pipeline Provisioning & Management
 *
 * Full-screen modal shown while a provisioning SSE stream is running.
 * Contains a progress bar, scrollable log, copy-log button, and dismiss control.
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Copy, Check, ExternalLink } from 'lucide-react';
import type { SseDoneEvent } from '@shared/types/telemetryPipeline';
import { useDraggable } from '../ui/useDraggable';

interface ProvisioningModalProps {
  title: string;
  logLines: string[];
  doneEvent: SseDoneEvent | null;
  isStreaming: boolean;
  error?: string | null;
  onClose: () => void;
}

const ProvisioningModal: React.FC<ProvisioningModalProps> = ({
  title,
  logLines,
  doneEvent,
  isStreaming,
  error,
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const { transform, onHandleMouseDown } = useDraggable({ handleSelector: '[data-modal-drag-handle]' });

  // Scroll log container to bottom on each new line
  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logLines]);

  // Close on Escape (only when not streaming)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isStreaming) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isStreaming, onClose]);

  const copyLog = () => {
    navigator.clipboard.writeText(logLines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const statusColor =
    doneEvent?.exitCode === 0
      ? 'text-green-600 dark:text-green-400'
      : doneEvent?.exitCode === 1
        ? 'text-red-600 dark:text-red-400'
        : doneEvent?.exitCode === 2
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-[var(--color-text-secondary)]';

  const statusLabel =
    doneEvent?.exitCode === 0
      ? 'Provisioning complete'
      : doneEvent?.exitCode === 1
        ? 'Provisioning failed'
        : doneEvent?.exitCode === 2
          ? 'Provisioned — manual steps required'
          : isStreaming
            ? 'Provisioning…'
            : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-transparent p-4">
      <div
        onMouseDown={onHandleMouseDown}
        className="flex w-full max-w-2xl flex-col rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-border)] shadow-2xl"
        style={{ maxHeight: '90vh', transform }}>

        {/* ── Header ── */}
        <div
          data-modal-drag-handle
          className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-4 cursor-move select-none">
          <div>
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{title}</h2>
            {statusLabel && (
              <p className={`mt-0.5 text-xs font-medium ${statusColor}`}>{statusLabel}</p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isStreaming}
            title={isStreaming ? 'Wait for provisioning to finish' : 'Close'}
            className="rounded-md p-1.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Progress bar ── */}
        <div className="h-1 w-full bg-[var(--color-bg-subtle)]">
          {isStreaming ? (
            <div className="h-1 animate-pulse bg-blue-500" style={{ width: '40%' }} />
          ) : doneEvent?.exitCode === 0 ? (
            <div className="h-1 w-full bg-green-500" />
          ) : doneEvent?.exitCode === 1 ? (
            <div className="h-1 w-full bg-red-500" />
          ) : doneEvent?.exitCode === 2 ? (
            <div className="h-1 w-full bg-amber-500" />
          ) : null}
        </div>

        {/* ── Log area ── */}
        <div
          ref={containerRef}
          className="flex-1 overflow-y-auto bg-[var(--color-bg-sunken)] p-4"
          style={{ scrollBehavior: 'auto', minHeight: 200 }}
        >
          {logLines.length === 0 && isStreaming && (
            <p className="font-mono text-xs text-[var(--color-text-secondary)]">Starting…</p>
          )}
          {error && !isStreaming && logLines.length === 0 && (
            <p className="font-mono text-xs text-[var(--color-status-danger)]">{error}</p>
          )}
          {logLines.map((line, i) => (
            <div key={i} className="whitespace-pre-wrap break-words font-mono text-xs leading-5 text-[var(--color-text-primary)]">
              {line}
            </div>
          ))}
        </div>

        {/* ── Manual steps (exit code 2) ── */}
        {doneEvent?.exitCode === 2 && doneEvent.manualSteps && doneEvent.manualSteps.length > 0 && (
          <div className="space-y-1 border-t border-[var(--color-border)] bg-amber-50 p-4 dark:bg-amber-900/10">
            <p className="mb-2 text-xs font-medium text-amber-700 dark:text-amber-400">
              The following capacities require manual Eventstream setup:
            </p>
            {doneEvent.manualSteps.map((step) => (
              <details key={step.capacityId} className="rounded border border-amber-200 dark:border-amber-800">
                <summary className="cursor-pointer select-none px-3 py-2 font-mono text-xs text-amber-800 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30">
                  {step.capacityId}
                </summary>
                <div className="space-y-2 px-3 pb-3 pt-2">
                  <p className="whitespace-pre-line text-xs text-[var(--color-text-secondary)]">
                    {step.instructions}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={step.portalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[var(--color-accent)] hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Open Eventstream Portal
                    </a>
                    {step.destinationHubConnectionString && (
                      <button
                        onClick={() => navigator.clipboard.writeText(step.destinationHubConnectionString!)}
                        className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-0.5 font-mono text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
                      >
                        <Copy className="h-3 w-3" />
                        Copy connection string
                      </button>
                    )}
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="flex items-center justify-between border-t border-[var(--color-border)] px-5 py-3">
          <button
            onClick={copyLog}
            disabled={logLines.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy log'}
          </button>

          <button
            onClick={onClose}
            disabled={isStreaming}
            className="rounded-lg bg-[var(--color-accent)] px-4 py-1.5 text-sm font-medium text-white hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isStreaming ? 'Running…' : 'Dismiss'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProvisioningModal;
